import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Users, Search, Plus } from 'lucide-react';
import { GroupList } from '@/components/customer/GroupList';
import { GroupChat } from '@/components/customer/GroupChat';
import { CreatePost } from '@/components/customer/CreatePost';
import { CreateGroupDialog } from '@/components/customer/CreateGroupDialog';
import { EditGroupDialog } from '@/components/customer/EditGroupDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { subscribeToGroupUpdates } from '@/lib/firebase/queries/groups';
import { getUserPersonalPermissions } from '@/lib/firebase/queries/permissions';
import { useOrganization } from '@/hooks/use-organization';
import type { Group } from '@/types/support';

export function SupportPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Mobile parity (owner decision 2026-10-08): the ACTIVE organization's
  // groups + personal groups, as SupportGroups and the nav badge
  // (useGroupsUnreadTotal) — this page used to list every organization's.
  const { selectedOrganization, loading: organizationLoading } = useOrganization();
  const organizationId = selectedOrganization?.id;

  // Groups created here are personal (no organization), so only the super
  // admin's setting applies — an organization's override cannot block them.
  const [canCreatePersonal, setCanCreatePersonal] = useState(false);
  useEffect(() => {
    if (!user?.uid) return;
    let alive = true;
    getUserPersonalPermissions(user.uid).then((p) => {
      if (alive) setCanCreatePersonal(p.actions.createSupportGroup !== false);
    });
    return () => { alive = false; };
  }, [user?.uid]);

  useEffect(() => {
    // Wait for the active organization, or the list would briefly be unscoped.
    if (organizationLoading) return;
    setLoading(true);
    const unsubscribe = subscribeToGroupUpdates(
      (updatedGroups) => {
        setGroups(updatedGroups);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching groups:', error);
        setLoading(false);
      },
      organizationId,
      { includePersonal: true }
    );

    return () => unsubscribe();
  }, [organizationId, organizationLoading]);

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleGroupUpdate = () => {
    setShowEditGroup(false);
    setSelectedGroup(null);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-semibold">{t('support.supportGroups')}</h1>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('support.searchGroups')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-full sm:w-[200px]"
                />
              </div>
              {canCreatePersonal && (
              <Button
                onClick={() => setShowCreateGroup(true)}
                className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('support.createGroup')}
              </Button>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div>
            <GroupList
              groups={filteredGroups}
              onOpenChat={(group) => {
                setSelectedGroup(group);
                setShowChat(true);
              }}
              onOpenPosts={(group) => {
                setSelectedGroup(group);
                setShowCreatePost(true);
              }}
              onEditGroup={(group) => {
                setSelectedGroup(group);
                setShowEditGroup(true);
              }}
            />
          </div>
        </div>
      </CardContent>

      {showChat && selectedGroup && (
        <GroupChat
          group={selectedGroup}
          open={showChat}
          onOpenChange={setShowChat}
        />
      )}

      {showCreatePost && selectedGroup && (
        <CreatePost
          group={selectedGroup}
          open={showCreatePost}
          onOpenChange={setShowCreatePost}
        />
      )}

      <CreateGroupDialog
        open={showCreateGroup}
        onOpenChange={setShowCreateGroup}
        onSuccess={(createdGroup?: Group) => {
          // Show the new group at once, as SupportGroups does.
          if (createdGroup) {
            setGroups((prev) => (prev.some((g) => g.id === createdGroup.id) ? prev : [createdGroup, ...prev]));
          }
          setShowCreateGroup(false);
        }}
      />

      {showEditGroup && selectedGroup && (
        <EditGroupDialog
          group={selectedGroup}
          open={showEditGroup}
          onOpenChange={setShowEditGroup}
          onSuccess={handleGroupUpdate}
        />
      )}
    </Card>
  );
}